import React, { useState, useEffect, useCallback } from 'react';

// Formats a Date using its LOCAL components (not toISOString, which
// converts to UTC first — that rolls the date forward in the evening for
// anyone west of UTC, e.g. Meyers Plus in San Francisco).
function toLocalDateInputValue(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

// Parses a "YYYY-MM-DD" <input type="date"> value as LOCAL midnight —
// `new Date("YYYY-MM-DD")` parses as UTC midnight instead, which in a
// negative UTC offset actually rolls back to the previous day's evening.
function parseLocalDateInput(dateString) {
  const [y, m, d] = dateString.split('-').map(Number);
  return new Date(y, m - 1, d);
}

// Mirrors the backend's addBusinessDays — used to auto-populate Due Date
// live as the admin edits Date Received, without a round-trip to the server.
function addBusinessDaysLocal(startDate, days) {
  const result = new Date(startDate);
  let added = 0;
  while (added < days) {
    result.setDate(result.getDate() + 1);
    const day = result.getDay();
    if (day !== 0 && day !== 6) added += 1; // skip weekends
  }
  return result;
}

// ─── CA+ Intake Tab (CA Admin / Natalie's screen) ──────────────────────────
// Companion to CaPlusTab.js — this is the gatekeeper-facing side: the full
// unfiltered queue, plus logging new Submittals/RFIs and assigning a reviewer.
//
// Rendered from inside CaPlusTab.js via the "Switch to Admin View" button,
// which only appears for users on FirmProfile.caPlusAdminEmails. No separate
// App.js wiring needed — this is not a top-level tab.
//
// Real access control: every route this component calls
// (caPlusIntakeRoutes.js) is protected server-side by requireCaPlusAdmin,
// which checks the same caPlusAdminEmails list independently of the frontend.
// The button being hidden is a convenience, not the security boundary.
// ─────────────────────────────────────────────────────────────────────────

const API_BASE = 'https://apispeccheck.zonetap.com';

const styles = {
  card: {
    background: 'white', border: '1px solid #e5e7eb', borderRadius: '10px',
    padding: '20px', marginBottom: '16px',
  },
  headerRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap', gap: '10px' },
  tabRow: { display: 'flex', gap: '8px', marginBottom: '16px' },
  smallTab: (active) => ({
    padding: '8px 16px', borderRadius: '8px', border: active ? '2px solid #D4AF37' : '2px solid #e5e7eb',
    backgroundColor: active ? '#fffbeb' : 'white', cursor: 'pointer', fontSize: '13px', fontWeight: '700', color: '#000000',
  }),
  listRow: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px',
    border: '1px solid #e5e7eb', borderRadius: '8px', marginBottom: '8px', backgroundColor: '#fafafa',
  },
  listRowTitle: { fontSize: '14px', fontWeight: '600', color: '#000000' },
  listRowMeta: { fontSize: '12px', color: '#6b7280', marginTop: '2px' },
  badge: (color) => ({
    fontSize: '11px', fontWeight: '700', padding: '3px 10px', borderRadius: '12px',
    backgroundColor: color + '22', color,
  }),
  input: {
    width: '100%', padding: '10px', border: '2px solid #d1d5db', borderRadius: '8px',
    fontSize: '14px', fontFamily: 'inherit', boxSizing: 'border-box', marginBottom: '12px',
  },
  textarea: {
    width: '100%', minHeight: '90px', padding: '10px', border: '2px solid #d1d5db',
    borderRadius: '8px', fontSize: '14px', fontFamily: 'inherit', boxSizing: 'border-box', resize: 'vertical', marginBottom: '12px',
  },
  select: {
    width: '100%', padding: '10px', border: '2px solid #d1d5db', borderRadius: '8px',
    fontSize: '14px', fontFamily: 'inherit', boxSizing: 'border-box', marginBottom: '12px', backgroundColor: 'white',
  },
  label: { display: 'block', fontSize: '13px', fontWeight: '600', color: '#374151', marginBottom: '6px' },
  row2: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '12px' },
  primaryBtn: {
    backgroundColor: '#D4AF37', color: '#000000', fontWeight: '700', padding: '10px 20px',
    borderRadius: '8px', border: 'none', cursor: 'pointer', fontSize: '14px',
  },
  secondaryBtn: {
    backgroundColor: 'white', color: '#374151', fontWeight: '600', padding: '10px 18px',
    borderRadius: '8px', border: '1px solid #d1d5db', cursor: 'pointer', fontSize: '14px', marginLeft: '8px',
  },
    fileBtn: {
    display: 'inline-block', padding: '9px 16px', fontSize: '13px', fontWeight: '600',
    color: '#374151', backgroundColor: '#f3f4f6', border: '1px solid #d1d5db', borderRadius: '6px',
    cursor: 'pointer', marginBottom: '8px',
  },
  banner: (bg, fg, border) => ({
    padding: '12px 16px', backgroundColor: bg, color: fg, border: `1px solid ${border}`,
    borderRadius: '8px', fontSize: '13px', marginBottom: '16px',
  }),
};

const STATUS_COLORS = { received: '#3B82F6', in_review: '#F59E0B', returned: '#22C55E' };

function ProjectPicker({
  value, onChange, projects, offices,
  showNewProject, setShowNewProject,
  newProjectNumber, setNewProjectNumber,
  newProjectName, setNewProjectName,
  newProjectOffice, setNewProjectOffice,
  createProject,
}) {
  return (
    <div>
      <label style={styles.label}>Project</label>
      <select style={styles.select} value={value} onChange={(e) => onChange(e.target.value)}>
        <option value="">Select a project...</option>
        {projects.map((p) => (
          <option key={p._id} value={p._id}>{p.projectNumber} — {p.projectName}</option>
        ))}
      </select>
    </div>
  );
}

function FileButton({ accept, multiple, onChange, label }) {
  return (
    <div style={{ marginBottom: '12px' }}>
      <label style={styles.fileBtn}>
        {label || (multiple ? '+ Choose Files' : '+ Choose File')}
        <input type="file" accept={accept} multiple={multiple} onChange={onChange} style={{ display: 'none' }} />
      </label>
    </div>
  );
}

function EmployeePicker({ employees, onSelect }) {
  if (!employees || employees.length === 0) return null;
  return (
    <div style={{ marginBottom: '10px' }}>
      <label style={styles.label}>Select Reviewer (from team)</label>
      <p style={{ fontSize: '12px', color: '#6b7280', marginTop: '-4px', marginBottom: '6px' }}>
        Optional — picking someone fills in their name and email below automatically.
      </p>
      <select
        style={styles.select}
        value=""
        onChange={(e) => {
          const emp = employees.find((emp) => emp.email === e.target.value);
          if (emp) onSelect(emp);
          e.target.value = '';
        }}
      >
        <option value="">Select a reviewer...</option>
        {employees.map((emp) => (
          <option key={emp.email} value={emp.email}>
            {emp.name}{emp.title ? ` — ${emp.title}` : ''}
          </option>
        ))}
      </select>
    </div>
  );
}

export default function CaPlusIntakeTab({ user }) {
  const userId = user?.email || user?.sub || 'anonymous';

  const [mode, setMode] = useState('queue'); // queue | new-submittal | new-rfi | new-for | manage-projects

    // Browser back button steps back to the queue instead of leaving the
    // app entirely — mirrors the same fix on the reviewer side (CaPlusTab.js).
    useEffect(() => {
      window.history.pushState({ caPlusView: 'queue' }, '');
    }, []);

    useEffect(() => {
      const handlePopState = () => {
        if (mode !== 'queue') {
          setMode('queue');
        } else {
          // Already at the floor — re-assert our own history entry instead
          // of letting the back button leave the app.
          window.history.pushState({ caPlusView: 'queue' }, '');
        }
      };
      window.addEventListener('popstate', handlePopState);
      return () => window.removeEventListener('popstate', handlePopState);
    }, [mode]);
  const [importFile, setImportFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [editingProject, setEditingProject] = useState(null); // the project object currently being edited, or null
  const [editForm, setEditForm] = useState({ projectNumber: '', projectName: '', projectManager: '', office: '' });
  const [offices, setOffices] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [officeFilter, setOfficeFilter] = useState('');
  const [projects, setProjects] = useState([]);

  const [queue, setQueue] = useState({ submittals: [], rfis: [] });
  const [queueTab, setQueueTab] = useState('submittals');
  const [queueSearch, setQueueSearch] = useState('');
  const [queueSort, setQueueSort] = useState('date-desc');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [successMsg, setSuccessMsg] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // Admin detail view state
  const [adminSelectedItem, setAdminSelectedItem] = useState(null);
  const [adminSelectedType, setAdminSelectedType] = useState(null); // 'submittal' | 'rfi'
  const [editingDates, setEditingDates] = useState(false);
  const [dateForm, setDateForm] = useState({ dateReceived: '', dueDate: '', dateReturned: '', notes: '' });
  const [stampOptions, setStampOptions] = useState([]);
  const [adminReviewerName, setAdminReviewerName] = useState('');
  const [adminReviewerEmail, setAdminReviewerEmail] = useState('');
  const [adminSaving, setAdminSaving] = useState(false);

  const [adminEditForNumber, setAdminEditForNumber] = useState('');
  const [adminEditAuthorName, setAdminEditAuthorName] = useState('');
  const [adminEditDateField, setAdminEditDateField] = useState('');

  // New-project inline form state
  const [showNewProject, setShowNewProject] = useState(false);
  const [newProjectNumber, setNewProjectNumber] = useState('');
  const [newProjectName, setNewProjectName] = useState('');
  const [newProjectOffice, setNewProjectOffice] = useState('');

  // Submittal file state
  const [sSpecFiles, setSSpecFiles] = useState([]);
  const [sSubmittalFile, setSSubmittalFile] = useState(null);
  const [sDrawings, setSDrawings] = useState([]);

  // Submittal form state
  const [sProjectId, setSProjectId] = useState('');
  const [sName, setSName] = useState('');
  const [sType, setSType] = useState('product');
  const [sDateReceived, setSDateReceived] = useState(() => toLocalDateInputValue(new Date()));
  const [sDueDate, setSDueDate] = useState(() => toLocalDateInputValue(addBusinessDaysLocal(new Date(), 10)));
  const [sNotes, setSNotes] = useState('');
  const [sPrimarySpecFile, setSPrimarySpecFile] = useState(null);
  const [sReviewer, setSReviewer] = useState('');
  const [sReviewerEmail, setSReviewerEmail] = useState('');

  // RFI file state
  const [rRfiFile, setRRfiFile] = useState(null);
  const [rProjectDocsFile, setRProjectDocsFile] = useState(null);
  const [rDrawings, setRDrawings] = useState([]);

  // Field Report form state
  const [fProjectId, setFProjectId] = useState('');
  const [fNumber, setFNumber] = useState('');
  const [fAuthorName, setFAuthorName] = useState('');
  const [fDateField, setFDateField] = useState('');
  const [fReportFile, setFReportFile] = useState(null);

  // RFI form state
  const [rProjectId, setRProjectId] = useState('');
  const [rName, setRName] = useState('');
  const [rDateReceived, setRDateReceived] = useState(() => toLocalDateInputValue(new Date()));
  const [rDueDate, setRDueDate] = useState(() => toLocalDateInputValue(addBusinessDaysLocal(new Date(), 10)));
  const [rNotes, setRNotes] = useState('');
  const [rReviewer, setRReviewer] = useState('');
  const [rReviewerEmail, setRReviewerEmail] = useState('');

  // Firm profile — for offices list
  useEffect(() => {
    if (!userId || userId === 'anonymous') return;
    fetch(`${API_BASE}/api/firm-profile?userId=${encodeURIComponent(userId)}`)
      .then((r) => r.json())
      .then((data) => {
        setOffices(data.profile?.offices || []);
        setStampOptions(data.profile?.caPlusStampOptions || []);
      })
      .catch(() => {});
  }, [userId]);

  // Employee list — for the reviewer picker
  useEffect(() => {
    if (!userId || userId === 'anonymous') return;
    fetch(`${API_BASE}/api/ca-plus/employees?userId=${encodeURIComponent(userId)}`)
      .then((r) => r.json())
      .then((data) => setEmployees(data.employees || []))
      .catch(() => {});
  }, [userId]);

  const loadProjects = useCallback(() => {
    const params = new URLSearchParams({ userId });
    if (officeFilter) params.set('office', officeFilter);
    fetch(`${API_BASE}/api/ca-plus/projects?${params.toString()}`)
      .then((r) => r.json())
      .then((data) => setProjects(data.projects || []))
      .catch(() => {});
  }, [userId, officeFilter]);

  const loadQueue = useCallback(() => {
    setLoading(true);
    setError(null);
    const params = new URLSearchParams({ userId });
    if (officeFilter) params.set('office', officeFilter);
    fetch(`${API_BASE}/api/ca-plus/intake/queue?${params.toString()}`)
      .then((r) => {
        if (!r.ok) throw new Error('Failed to load queue');
        return r.json();
      })
      .then((data) => setQueue({ submittals: data.submittals || [], rfis: data.rfis || [], fors: data.fors || [] }))
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [userId, officeFilter]);

  useEffect(() => { loadProjects(); loadQueue(); }, [loadProjects, loadQueue]);

  const openAdminDetail = (item, type) => {
    setAdminSelectedItem(item);
    setAdminSelectedType(type);
    setAdminReviewerName(item.reviewerName || '');
    setAdminReviewerEmail(item.reviewerEmail || '');
    setAdminEditForNumber(item.forNumber || '');
    setAdminEditAuthorName(item.authorName || '');
    setAdminEditDateField(item.dateField ? new Date(item.dateField).toISOString().slice(0, 10) : '');
    setMode('admin-detail');
    window.history.pushState({ caPlusView: 'admin-detail' }, '');
  };

  const reassignReviewer = async () => {
    if (!adminReviewerName.trim() || !adminReviewerEmail.trim()) {
      alert('Reviewer name and email are required');
      return;
    }
    setAdminSaving(true);
    try {
            const endpoint = adminSelectedType === 'submittal' ? 'submittals' : adminSelectedType === 'rfi' ? 'rfis' : 'fors';
      const res = await fetch(`${API_BASE}/api/ca-plus/${endpoint}/${adminSelectedItem._id}/reassign`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId, reviewerName: adminReviewerName.trim(), reviewerEmail: adminReviewerEmail.trim() }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to reassign reviewer');
      setSuccessMsg(`Reassigned to ${adminReviewerName.trim()}.`);
      setMode('queue');
      loadQueue();
    } catch (e) {
      alert(e.message);
    } finally {
      setAdminSaving(false);
    }
  };

  const startEditDates = () => {
    setDateForm({
      dateReceived: adminSelectedItem.dateReceived ? toLocalDateInputValue(new Date(adminSelectedItem.dateReceived)) : '',
      dueDate: adminSelectedItem.dueDate ? toLocalDateInputValue(new Date(adminSelectedItem.dueDate)) : '',
      dateReturned: adminSelectedItem.dateReturned ? toLocalDateInputValue(new Date(adminSelectedItem.dateReturned)) : '',
      notes: adminSelectedItem.notes || '',
    });
    setEditingDates(true);
  };

  const saveDates = async () => {
    setAdminSaving(true);
    try {
      const endpoint = adminSelectedType === 'submittal' ? 'submittals' : 'rfis';
      const body = {
        userId,
        dateReceived: dateForm.dateReceived || null,
        dueDate: dateForm.dueDate || null,
        dateReturned: dateForm.dateReturned || null,
        notes: dateForm.notes,
      };
      const res = await fetch(`${API_BASE}/api/ca-plus/${endpoint}/${adminSelectedItem._id}/dates`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to save dates');
      setAdminSelectedItem(data.submittal || data.rfi);
      setEditingDates(false);
    } catch (e) {
      alert(e.message);
    } finally {
      setAdminSaving(false);
    }
  };

    const saveForEdits = async () => {
    setAdminSaving(true);
    try {
      const res = await fetch(`${API_BASE}/api/ca-plus/fors/${adminSelectedItem._id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId,
          forNumber: adminEditForNumber.trim(),
          authorName: adminEditAuthorName.trim(),
          dateField: adminEditDateField,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to save changes');
      setSuccessMsg('Field report updated.');
      setMode('queue');
      loadQueue();
    } catch (e) {
      alert(e.message);
    } finally {
      setAdminSaving(false);
    }
  };

  const deleteItem = async () => {
    if (!window.confirm('Delete this item permanently? This cannot be undone.')) return;
    setAdminSaving(true);
    try {
      const endpoint = adminSelectedType === 'submittal' ? 'submittals' : 'rfis';
      const res = await fetch(`${API_BASE}/api/ca-plus/${endpoint}/${adminSelectedItem._id}?userId=${encodeURIComponent(userId)}`, {
        method: 'DELETE',
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to delete');
      setSuccessMsg('Item deleted.');
      setMode('queue');
      loadQueue();
    } catch (e) {
      alert(e.message);
    } finally {
      setAdminSaving(false);
    }
  };

  const createProject = async () => {
    if (!newProjectNumber.trim() || !newProjectName.trim()) {
      alert('Project number and name are required');
      return;
    }
    try {
      const res = await fetch(`${API_BASE}/api/ca-plus/projects`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId, projectNumber: newProjectNumber.trim(), projectName: newProjectName.trim(),
          office: newProjectOffice || undefined,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to create project');
      setProjects((prev) => [...prev, data.project]);
      setSProjectId(data.project._id);
      setRProjectId(data.project._id);
      setNewProjectNumber(''); setNewProjectName(''); setNewProjectOffice('');
      setShowNewProject(false);
    } catch (e) {
      alert(e.message);
    }
  };

  const importProjects = async () => {
    if (!importFile) return;
    setImporting(true);
    setImportResult(null);
    try {
      const formData = new FormData();
      formData.append('userId', userId);
      formData.append('file', importFile);
      const res = await fetch(`${API_BASE}/api/ca-plus/projects/import`, {
        method: 'POST',
        body: formData,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to import projects');
      setImportResult(data);
      setImportFile(null);
      loadProjects();
    } catch (e) {
      alert(e.message);
    } finally {
      setImporting(false);
    }
  };

  const startEditProject = (project) => {
    setEditingProject(project);
    setEditForm({
      projectNumber: project.projectNumber || '',
      projectName: project.projectName || '',
      projectManager: project.projectManager || '',
      office: project.office || '',
    });
  };

  const saveEditProject = async () => {
    if (!editForm.projectNumber.trim() || !editForm.projectName.trim()) {
      alert('Project number and name are required');
      return;
    }
    try {
      const res = await fetch(`${API_BASE}/api/ca-plus/projects/${editingProject._id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId, ...editForm }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to save changes');
      setEditingProject(null);
      loadProjects();
    } catch (e) {
      alert(e.message);
    }
  };

  const deleteProject = async (project) => {
    if (!window.confirm(`Delete project ${project.projectNumber} — ${project.projectName}? This cannot be undone.`)) return;
    try {
      const res = await fetch(`${API_BASE}/api/ca-plus/projects/${project._id}?userId=${encodeURIComponent(userId)}`, {
        method: 'DELETE',
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to delete project');
      loadProjects();
    } catch (e) {
      alert(e.message);
    }
  };

  const submitSubmittal = async () => {
    if (!sProjectId || !sName.trim() || !sReviewer.trim() || !sReviewerEmail.trim()) {
      alert('Project, submittal name, reviewer name, and reviewer email are required');
      return;
    }
    if ((sSpecFiles.length === 0 && !sPrimarySpecFile) || !sSubmittalFile) {
      alert('At least one spec file (bulk or Primary Spec) and the submittal file are required for CA+ review');
      return;
    }
    setSubmitting(true);
    try {
      const formData = new FormData();
      formData.append('userId', userId);
      formData.append('projectId', sProjectId);
      formData.append('submittalName', sName.trim());
      formData.append('submittalType', sType);
      formData.append('dateReceived', sDateReceived);
      formData.append('dueDate', sDueDate);
      if (sNotes.trim()) formData.append('notes', sNotes.trim());
      if (sPrimarySpecFile) formData.append('primarySpec', sPrimarySpecFile);
      formData.append('reviewerName', sReviewer.trim());
      formData.append('reviewerEmail', sReviewerEmail.trim());
      sSpecFiles.forEach((f) => formData.append('specs', f));
      formData.append('submittal', sSubmittalFile);
      sDrawings.forEach((f) => formData.append('drawings', f));

      // NOTE: do NOT set a Content-Type header here — the browser sets the
      // correct multipart boundary automatically. Setting it manually breaks upload.
      const res = await fetch(`${API_BASE}/api/ca-plus/intake/submittals`, {
        method: 'POST',
        body: formData,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to log submittal');
      setSuccessMsg(`Logged "${sName}" and notified ${sReviewer}.`);
      setSName(''); setSPrimarySpecFile(null); setSSpecFiles([]); setSReviewer(''); setSReviewerEmail('');
      setSSpecFiles([]); setSSubmittalFile(null); setSDrawings([]);
      setMode('queue');
      loadQueue();
    } catch (e) {
      alert(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  const submitRfi = async () => {
    if (!rProjectId || !rName.trim() || !rReviewer.trim() || !rReviewerEmail.trim()) {
      alert('Project, RFI name, reviewer name, and reviewer email are required');
      return;
    }
    if (!rRfiFile || !rProjectDocsFile) {
      alert('RFI document and Primary Spec File are required for CA+ review');
      return;
    }
    setSubmitting(true);
    try {
      const formData = new FormData();
      formData.append('userId', userId);
      formData.append('projectId', rProjectId);
      formData.append('rfiName', rName.trim());
      formData.append('dateReceived', rDateReceived);
      formData.append('dueDate', rDueDate);
      if (rNotes.trim()) formData.append('notes', rNotes.trim());
      formData.append('reviewerName', rReviewer.trim());
      formData.append('reviewerEmail', rReviewerEmail.trim());
      formData.append('rfi', rRfiFile);
      if (rProjectDocsFile) formData.append('projectDocs', rProjectDocsFile);
      rDrawings.forEach((f) => formData.append('drawings', f));

      const res = await fetch(`${API_BASE}/api/ca-plus/intake/rfis`, {
        method: 'POST',
        body: formData,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to log RFI');
      setSuccessMsg(`Logged "${rName}" and notified ${rReviewer}.`);
      setRName(''); setRReviewer(''); setRReviewerEmail('');
      setRRfiFile(null); setRProjectDocsFile(null); setRDrawings([]);
      setMode('queue');
      loadQueue();
    } catch (e) {
      alert(e.message);
    } finally {
      setSubmitting(false);
    }
  };

    const submitFor = async () => {
    if (!fProjectId || !fNumber.trim() || !fAuthorName.trim()) {
      alert('Project, report number, and author are required');
      return;
    }
    setSubmitting(true);
    try {
      const formData = new FormData();
      formData.append('userId', userId);
      formData.append('projectId', fProjectId);
      formData.append('forNumber', fNumber.trim());
      formData.append('authorName', fAuthorName.trim());
      if (fDateField) formData.append('dateField', fDateField);
      if (fReportFile) formData.append('report', fReportFile);

      const res = await fetch(`${API_BASE}/api/ca-plus/intake/fors`, {
        method: 'POST',
        body: formData,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to log field report');
      setSuccessMsg(`Logged field report ${fNumber}.`);
      setFNumber(''); setFAuthorName(''); setFDateField(''); setFReportFile(null);
      setMode('queue');
      loadQueue();
    } catch (e) {
      alert(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  useEffect(() => {
    if (successMsg) {
      const t = setTimeout(() => setSuccessMsg(null), 5000);
      return () => clearTimeout(t);
    }
  }, [successMsg]);


  return (
    <div>
      <div style={styles.headerRow}>
        <h2 style={{ fontSize: '20px', fontWeight: '800', color: '#000000', margin: 0 }}>CA+ Admin</h2>
        <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
          {offices.length > 1 && (
            <select value={officeFilter} onChange={(e) => setOfficeFilter(e.target.value)} style={{ ...styles.select, width: 'auto', marginBottom: 0, padding: '8px 12px' }}>
              <option value="">All Offices</option>
              {offices.map((o) => <option key={o.code} value={o.code}>{o.name || o.code}</option>)}
            </select>
          )}
          <button style={mode === 'new-submittal' ? styles.primaryBtn : styles.secondaryBtn} onClick={() => { setMode('new-submittal'); window.history.pushState({ caPlusView: 'new-submittal' }, ''); }}>+ Log Submittal</button>
          <button style={mode === 'new-rfi' ? styles.primaryBtn : styles.secondaryBtn} onClick={() => { setMode('new-rfi'); window.history.pushState({ caPlusView: 'new-rfi' }, ''); }}>+ Log RFI</button>
          <button style={mode === 'new-for' ? styles.primaryBtn : styles.secondaryBtn} onClick={() => { setMode('new-for'); window.history.pushState({ caPlusView: 'new-for' }, ''); }}>+ Log Field Report</button>
          <button style={mode === 'manage-projects' ? styles.primaryBtn : styles.secondaryBtn} onClick={() => { setMode('manage-projects'); window.history.pushState({ caPlusView: 'manage-projects' }, ''); }}>Manage Projects</button>
        </div>
      </div>

      {successMsg && <div style={styles.banner('#f0fdf4', '#166534', '#86efac')}>✓ {successMsg}</div>}

      {mode === 'queue' && (
        <>
          <div style={styles.tabRow}>
            <div style={styles.smallTab(queueTab === 'submittals')} onClick={() => setQueueTab('submittals')}>
              Submittals ({queue.submittals.length})
            </div>
            <div style={styles.smallTab(queueTab === 'rfis')} onClick={() => setQueueTab('rfis')}>
              RFIs ({queue.rfis.length})
            </div>
            <div style={styles.smallTab(queueTab === 'fors')} onClick={() => setQueueTab('fors')}>
              Field Reports ({(queue.fors || []).length})
            </div>
          </div>

          {loading && <p style={{ color: '#6b7280' }}>Loading...</p>}
          {error && <p style={{ color: '#dc2626' }}>{error}</p>}

          {!loading && !error && (() => {
            const q = queueSearch.trim().toLowerCase();
            let items = queue[queueTab] || [];

            if (q) {
              items = items.filter((item) => {
                const haystack = [
                  item.submittalName, item.rfiName, item.forNumber,
                  item.projectId?.projectNumber, item.projectId?.projectName,
                  item.reviewerName, item.authorName,
                ].filter(Boolean).join(' ').toLowerCase();
                return haystack.includes(q);
              });
            }

            const sorted = [...items].sort((a, b) => {
              if (queueSort === 'date-asc') return new Date(a.dateReceived || a.dateField || a.createdAt || 0) - new Date(b.dateReceived || b.dateField || b.createdAt || 0);
              if (queueSort === 'date-desc') return new Date(b.dateReceived || b.dateField || b.createdAt || 0) - new Date(a.dateReceived || a.dateField || a.createdAt || 0);
              if (queueSort === 'project') return (a.projectId?.projectNumber || '').localeCompare(b.projectId?.projectNumber || '');
              if (queueSort === 'name') {
                const nameA = a.submittalName || a.rfiName || a.forNumber || '';
                const nameB = b.submittalName || b.rfiName || b.forNumber || '';
                return nameA.localeCompare(nameB);
              }
              return 0;
            });

            return (
              <>
                <div style={{ display: 'flex', gap: '10px', marginBottom: '12px', flexWrap: 'wrap' }}>
                  <input
                    style={{ ...styles.input, marginBottom: 0, flex: '1 1 240px' }}
                    value={queueSearch}
                    onChange={(e) => setQueueSearch(e.target.value)}
                    placeholder="Search by name, project, or reviewer..."
                  />
                  <select
                    style={{ ...styles.select, marginBottom: 0, width: 'auto' }}
                    value={queueSort}
                    onChange={(e) => setQueueSort(e.target.value)}
                  >
                    <option value="date-desc">Newest First</option>
                    <option value="date-asc">Oldest First</option>
                    <option value="project">Project Number</option>
                    <option value="name">Name (A–Z)</option>
                  </select>
                </div>

                {sorted.length === 0 && (
                  <p style={{ color: '#6b7280', fontSize: '14px' }}>
                    {q ? `No results for "${queueSearch}".` : 'Nothing in the queue.'}
                  </p>
                )}

                {sorted.map((item) => {
                  const name = item.submittalName || item.rfiName || item.forNumber || 'Untitled';
                  const status = item.status || 'received';
                  const itemType = queueTab === 'submittals' ? 'submittal' : queueTab === 'rfis' ? 'rfi' : 'for';
                  const itemDate = item.dateReceived || item.dateField || item.createdAt;
            return (
              <div key={item._id} style={{ ...styles.listRow, cursor: 'pointer' }} onClick={() => openAdminDetail(item, itemType)}>
                <div>
                  <div style={styles.listRowTitle}>{name}</div>
                  <div style={styles.listRowMeta}>
                    {item.projectId?.projectNumber ? `${item.projectId.projectNumber} — ` : ''}
                    {item.projectId?.projectName || ''}
                    {itemType === 'for'
                      ? ` · Author: ${item.authorName || 'N/A'}`
                      : ` · Reviewer: ${item.reviewerName || 'Unassigned'}`}
                    {item.source?.system === 'procore' ? ' · via Procore' : ''}
                  </div>
                  {item.notes && (
                    <div style={{
                      fontSize: '11px', color: '#78350f', background: '#fffbeb', border: '1px solid #fde68a',
                      borderRadius: '4px', padding: '2px 6px', marginTop: '4px', display: 'inline-block',
                      maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    }}>
                      <strong>Note:</strong> {item.notes}
                    </div>
                  )}
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '4px' }}>
                  <span style={styles.badge(STATUS_COLORS[status] || '#6b7280')}>{status.replace('_', ' ')}</span>
                  {itemDate && (
                    <span style={{ fontSize: '11px', color: '#9ca3af' }}>
                      {new Date(itemDate).toLocaleDateString(undefined, { timeZone: 'UTC' })}
                    </span>
                  )}
                  {itemType !== 'for' && (() => {
                    const isReturned = status === 'returned' && typeof item.daysReviewed === 'number';
                    const daysReviewed = isReturned
                      ? item.daysReviewed
                      : itemDate
                        ? Math.floor((new Date() - new Date(itemDate)) / (1000 * 60 * 60 * 24))
                        : null;
                    if (daysReviewed === null) return null;
                    const heat = daysReviewed <= 4
                      ? { bg: '#dcfce7', text: '#166534' }
                      : daysReviewed <= 7
                        ? { bg: '#fef9c3', text: '#854d0e' }
                        : { bg: '#fee2e2', text: '#991b1b' };
                    return (
                      <span style={{ fontSize: '11px', fontWeight: '600', padding: '2px 6px', borderRadius: '4px', background: heat.bg, color: heat.text }}>
                        Days Reviewed: {daysReviewed}
                      </span>
                    );
                  })()}
                  {itemType === 'submittal' && (
                    <span style={{ fontSize: '11px', color: '#9ca3af' }}>
                      Action: {item.stampActionId === 'MULTIPLE' ? 'Multiple (Bluebeam)' : (stampOptions.find((o) => o.id === item.stampActionId)?.label || item.stampActionId || '—')}
                    </span>
                  )}
                  {itemType === 'rfi' && (
                    <span style={{ fontSize: '11px', color: '#9ca3af' }}>Action: N/A</span>
                  )}
                </div>
              </div>
            );
                })}
              </>
            );
          })()}
        </>
      )}

            {mode === 'admin-detail' && adminSelectedItem && (
        <div style={styles.card}>
          <button style={{ ...styles.secondaryBtn, marginLeft: 0, marginBottom: '16px' }} onClick={() => setMode('queue')}>
            ← Back to Queue
          </button>
          <h3 style={{ marginTop: 0 }}>
            {adminSelectedItem.submittalName || adminSelectedItem.rfiName}
          </h3>
          <div style={{ fontSize: '13px', color: '#6b7280', marginBottom: '6px' }}>
            {adminSelectedItem.projectId?.projectNumber} — {adminSelectedItem.projectId?.projectName}
            {adminSelectedItem.primarySpecSection ? ` · Spec ${adminSelectedItem.primarySpecSection}` : ''}
            {' · '}
            <span style={styles.badge(STATUS_COLORS[adminSelectedItem.status] || '#6b7280')}>
              {(adminSelectedItem.status || 'received').replace('_', ' ')}
            </span>
          </div>
          {editingDates ? (
            <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-end', flexWrap: 'wrap', marginBottom: '16px', padding: '10px', background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '6px' }}>
              <div>
                <div style={styles.label}>Received</div>
                <input type="date" style={styles.input} value={dateForm.dateReceived}
                  onChange={(e) => setDateForm({ ...dateForm, dateReceived: e.target.value })} />
              </div>
              <div>
                <div style={styles.label}>Due</div>
                <input type="date" style={styles.input} value={dateForm.dueDate}
                  onChange={(e) => setDateForm({ ...dateForm, dueDate: e.target.value })} />
              </div>
              <div>
                <div style={styles.label}>Returned</div>
                <input type="date" style={styles.input} value={dateForm.dateReturned}
                  onChange={(e) => setDateForm({ ...dateForm, dateReturned: e.target.value })} />
              </div>
              <div style={{ width: '100%' }}>
                <div style={styles.label}>Notes</div>
                <textarea style={{ ...styles.textarea, minHeight: '50px' }} value={dateForm.notes}
                  onChange={(e) => setDateForm({ ...dateForm, notes: e.target.value })}
                  placeholder="Note for the reviewer — not included in the output report" />
              </div>
              <button style={styles.primaryBtn} disabled={adminSaving} onClick={saveDates}>
                {adminSaving ? 'Saving…' : 'Save'}
              </button>
              <button style={styles.secondaryBtn} onClick={() => setEditingDates(false)}>Cancel</button>
            </div>
          ) : (
            <div style={{ fontSize: '12px', color: '#9ca3af', marginBottom: '16px' }}>
              {adminSelectedItem.dateReceived ? `Received: ${new Date(adminSelectedItem.dateReceived).toLocaleDateString(undefined, { timeZone: 'UTC' })}` : ''}
              {adminSelectedItem.dueDate ? ` · Due: ${new Date(adminSelectedItem.dueDate).toLocaleDateString(undefined, { timeZone: 'UTC' })}` : ''}
              {' · Returned: '}{adminSelectedItem.dateReturned ? new Date(adminSelectedItem.dateReturned).toLocaleDateString(undefined, { timeZone: 'UTC' }) : '—'}
              {' · Days Reviewed: '}{typeof adminSelectedItem.daysReviewed === 'number' ? adminSelectedItem.daysReviewed : '—'}
              {' · Action: '}{adminSelectedType === 'rfi' ? 'N/A' : (stampOptions.find((o) => o.id === adminSelectedItem.stampActionId)?.label || adminSelectedItem.stampActionId || '—')}
              {' '}
              <button style={{ background: 'none', border: 'none', color: '#6b7280', fontSize: '11px', cursor: 'pointer', textDecoration: 'underline', padding: 0, marginLeft: '4px' }} onClick={startEditDates}>
                Edit
              </button>
              {adminSelectedItem.notes && (
                <div style={{ marginTop: '6px', padding: '8px 10px', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: '5px', fontSize: '12px', color: '#78350f' }}>
                  <strong>Note:</strong> {adminSelectedItem.notes}
                </div>
              )}
            </div>
          )}

          {adminSelectedType === 'submittal' && (adminSelectedItem.aiDraftComments || []).length > 0 && (
            <div style={{ marginBottom: '20px' }}>
              <div style={styles.label}>CA+ Suggested Comments ({adminSelectedItem.aiDraftComments.length})</div>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                <thead>
                  <tr style={{ backgroundColor: '#fafafa' }}>
                    <th style={{ textAlign: 'left', padding: '8px', borderBottom: '2px solid #e5e7eb', width: '30%' }}>Spec Section</th>
                    <th style={{ textAlign: 'left', padding: '8px', borderBottom: '2px solid #e5e7eb' }}>Details</th>
                  </tr>
                </thead>
                <tbody>
                  {adminSelectedItem.aiDraftComments.map((c, i) => (
                    <tr key={i} style={{ borderBottom: '1px solid #e5e7eb' }}>
                      <td style={{ padding: '8px', fontWeight: '600', verticalAlign: 'top' }}>{c.specSection}</td>
                      <td style={{ padding: '8px', verticalAlign: 'top' }}>{c.text}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {adminSelectedType === 'submittal' && adminSelectedItem.drawingReviewNote && (
            <div style={{ marginBottom: '20px' }}>
              <div style={styles.label}>Drawing Review</div>
              <div style={{ padding: '8px 12px', backgroundColor: '#f0fdf4', border: '1px solid #86efac', borderRadius: '6px', fontSize: '13px' }}>
                {adminSelectedItem.drawingReviewNote}
              </div>
            </div>
          )}

          {adminSelectedType === 'rfi' && (adminSelectedItem.aiDraftResponse || []).length > 0 && (
            <div style={{ marginBottom: '20px' }}>
              <div style={styles.label}>CA+ Suggested Response</div>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                <thead>
                  <tr style={{ backgroundColor: '#fafafa' }}>
                    <th style={{ textAlign: 'left', padding: '8px', borderBottom: '2px solid #e5e7eb' }}>RFI #</th>
                    <th style={{ textAlign: 'left', padding: '8px', borderBottom: '2px solid #e5e7eb' }}>Spec Section</th>
                    <th style={{ textAlign: 'left', padding: '8px', borderBottom: '2px solid #e5e7eb' }}>Drawing Ref</th>
                    <th style={{ textAlign: 'left', padding: '8px', borderBottom: '2px solid #e5e7eb' }}>Response</th>
                  </tr>
                </thead>
                <tbody>
                  {adminSelectedItem.aiDraftResponse.map((r, i) => (
                    <tr key={i} style={{ borderBottom: '1px solid #e5e7eb' }}>
                      <td style={{ padding: '8px', fontWeight: '600', verticalAlign: 'top' }}>{r.rfiId}</td>
                      <td style={{ padding: '8px', verticalAlign: 'top' }}>{r.specSection}</td>
                      <td style={{ padding: '8px', verticalAlign: 'top' }}>{r.drawingRef}</td>
                      <td style={{ padding: '8px', verticalAlign: 'top' }}>{r.response}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {(adminSelectedItem.reviewerComments || []).length > 0 && (
            <div style={{ marginBottom: '20px' }}>
              <div style={styles.label}>Reviewer Comments</div>
              {adminSelectedItem.reviewerComments.map((c, i) => (
                <div key={i} style={{ padding: '8px 12px', backgroundColor: '#fafafa', border: '1px solid #e5e7eb', borderRadius: '6px', marginBottom: '6px', fontSize: '13px' }}>
                  {c.text} <span style={{ color: '#9ca3af', fontSize: '11px' }}>— {c.addedBy}</span>
                </div>
              ))}
            </div>
          )}

          {adminSelectedType !== 'for' && (
            <div style={{ borderTop: '1px solid #e5e7eb', paddingTop: '16px', marginTop: '16px' }}>
              <div style={styles.label}>Reassign Reviewer</div>
              <div style={styles.row2}>
                <input style={styles.input} value={adminReviewerName} onChange={(e) => setAdminReviewerName(e.target.value)} placeholder="Reviewer name" />
                <input style={styles.input} type="email" value={adminReviewerEmail} onChange={(e) => setAdminReviewerEmail(e.target.value)} placeholder="Reviewer email" />
              </div>
              <button style={styles.primaryBtn} onClick={reassignReviewer} disabled={adminSaving}>
                {adminSaving ? 'Saving...' : 'Save Reassignment'}
              </button>
            </div>
          )}

          {adminSelectedType === 'for' && (
            <div style={{ borderTop: '1px solid #e5e7eb', paddingTop: '16px', marginTop: '16px' }}>
              <div style={styles.label}>Edit Field Report</div>
              <input style={styles.input} value={adminEditForNumber} onChange={(e) => setAdminEditForNumber(e.target.value)} placeholder="Report number" />
              <input style={styles.input} value={adminEditAuthorName} onChange={(e) => setAdminEditAuthorName(e.target.value)} placeholder="Author" />
              <input style={styles.input} type="date" value={adminEditDateField} onChange={(e) => setAdminEditDateField(e.target.value)} />
              <button style={styles.primaryBtn} onClick={saveForEdits} disabled={adminSaving}>
                {adminSaving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          )}

          {adminSelectedType === 'submittal' && adminSelectedItem.status === 'returned' && (adminSelectedItem.reportUrl || adminSelectedItem.stampedUrl) && (
            <div style={{ borderTop: '1px solid #e5e7eb', paddingTop: '16px', marginTop: '16px' }}>
              <div style={styles.label}>Returned Files</div>
              <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                {adminSelectedItem.reportUrl && (
                  <a href={adminSelectedItem.reportUrl} target="_blank" rel="noopener noreferrer" style={{ ...styles.secondaryBtn, marginLeft: 0, textDecoration: 'none', display: 'inline-block' }}>
                    Download Report
                  </a>
                )}
                {adminSelectedItem.stampedUrl && (
                  <a href={adminSelectedItem.stampedUrl} target="_blank" rel="noopener noreferrer" style={{ ...styles.secondaryBtn, marginLeft: 0, textDecoration: 'none', display: 'inline-block' }}>
                    Download Stamped Document
                  </a>
                )}
              </div>
            </div>
          )}

          <div style={{ borderTop: '1px solid #e5e7eb', paddingTop: '16px', marginTop: '16px' }}>
            <button
              style={{ ...styles.secondaryBtn, marginLeft: 0, color: '#dc2626', borderColor: '#fca5a5' }}
              onClick={deleteItem}
              disabled={adminSaving}
            >
              Delete Item
            </button>
          </div>
        </div>
      )}

      {mode === 'new-submittal' && (
        <div style={styles.card}>
          <h3 style={{ marginTop: 0 }}>Log New Submittal</h3>
            <ProjectPicker
            value={sProjectId} onChange={setSProjectId}
            projects={projects} offices={offices}
            showNewProject={showNewProject} setShowNewProject={setShowNewProject}
            newProjectNumber={newProjectNumber} setNewProjectNumber={setNewProjectNumber}
            newProjectName={newProjectName} setNewProjectName={setNewProjectName}
            newProjectOffice={newProjectOffice} setNewProjectOffice={setNewProjectOffice}
            createProject={createProject}
          />
          <label style={styles.label}>Submittal Name</label>
          <input style={styles.input} value={sName} onChange={(e) => setSName(e.target.value)} placeholder="e.g. 230000-293.0-0 Mechanical Shop Drawings" />
          <div style={styles.row2}>
            <div>
              <label style={styles.label}>Date Received</label>
              <input
                type="date"
                style={styles.input}
                value={sDateReceived}
                onChange={(e) => {
                  setSDateReceived(e.target.value);
                  setSDueDate(toLocalDateInputValue(addBusinessDaysLocal(parseLocalDateInput(e.target.value), 10)));
                }}
              />
            </div>
            <div>
              <label style={styles.label}>Due Date</label>
              <input
                type="date"
                style={styles.input}
                value={sDueDate}
                onChange={(e) => setSDueDate(e.target.value)}
              />
            </div>
          </div>
          <div>
            <label style={styles.label}>Notes (visible to reviewer, not included in the output report)</label>
            <textarea style={styles.textarea} value={sNotes} onChange={(e) => setSNotes(e.target.value)} placeholder="Optional note for the reviewer..." />
          </div>
          <EmployeePicker employees={employees} onSelect={(emp) => { setSReviewer(emp.name); setSReviewerEmail(emp.email); }} />
          <div style={styles.row2}>
            <div>
              <label style={styles.label}>Reviewer Name</label>
              <input style={styles.input} value={sReviewer} onChange={(e) => setSReviewer(e.target.value)} placeholder="e.g. Jane Doe" />
            </div>
            <div>
              <label style={styles.label}>Reviewer Email</label>
              <input style={styles.input} type="email" value={sReviewerEmail} onChange={(e) => setSReviewerEmail(e.target.value)} placeholder="reviewer@company.com" />
            </div>
          </div>

          <label style={styles.label}>Submittal File</label>
          <FileButton
            accept=".pdf,.doc,.docx"
            onChange={(e) => {
              const file = e.target.files[0] || null;
              setSSubmittalFile(file);
              if (file && !sName.trim()) {
                const nameWithoutExt = file.name.replace(/\.[^/.]+$/, '');
                setSName(nameWithoutExt);
              }
            }}
            label="+ Choose Submittal File"
          />

          {sSubmittalFile && (
            <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '12px' }}>{sSubmittalFile.name}</div>
          )}

          <label style={styles.label}>Primary Spec File</label>
          <FileButton accept=".pdf,.doc,.docx" onChange={(e) => setSPrimarySpecFile(e.target.files[0] || null)} label="+ Choose Primary Spec File" />
          {sPrimarySpecFile && (
            <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '-8px', marginBottom: '12px' }}>{sPrimarySpecFile.name}</div>
          )}

          <label style={styles.label}>Related Spec Files (optional)</label>
          <p style={{ fontSize: '12px', color: '#6b7280', marginTop: '-4px', marginBottom: '8px' }}>Up to 20 files.</p>
          <FileButton accept=".pdf,.doc,.docx" multiple onChange={(e) => setSSpecFiles(Array.from(e.target.files))} label="+ Choose Related Spec Files" />

          {sSpecFiles.length > 0 && (
            <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '12px' }}>
              {sSpecFiles.length} file(s) selected: {sSpecFiles.map((f) => f.name).join(', ')}
            </div>
          )}

          <label style={styles.label}>Drawings (optional, up to 20)</label>

          <FileButton accept=".pdf,image/*" multiple onChange={(e) => setSDrawings(Array.from(e.target.files))} label="+ Choose Drawings" />

          {sDrawings.length > 0 && (
            <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '12px' }}>{sDrawings.length} drawing(s) selected</div>
          )}

          <button style={styles.primaryBtn} onClick={submitSubmittal} disabled={submitting}>
            {submitting ? 'Logging...' : 'Log Submittal & Notify Reviewer'}
          </button>
          <button
            style={styles.secondaryBtn}
            onClick={() => {
              setSProjectId(''); setSName(''); setSType('product');
              setSReviewer(''); setSReviewerEmail('');
              setSPrimarySpecFile(null); setSSpecFiles([]);
              setSSpecFiles([]); setSSubmittalFile(null); setSDrawings([]);
            }}
          >
            Start Over
          </button>
          <button style={styles.secondaryBtn} onClick={() => setMode('queue')}>Cancel</button>
        </div>
      )}

      {mode === 'new-rfi' && (
        <div style={styles.card}>
          <h3 style={{ marginTop: 0 }}>Log New RFI</h3>
            <ProjectPicker
            value={rProjectId} onChange={setRProjectId}
            projects={projects} offices={offices}
            showNewProject={showNewProject} setShowNewProject={setShowNewProject}
            newProjectNumber={newProjectNumber} setNewProjectNumber={setNewProjectNumber}
            newProjectName={newProjectName} setNewProjectName={setNewProjectName}
            newProjectOffice={newProjectOffice} setNewProjectOffice={setNewProjectOffice}
            createProject={createProject}
          />
          <label style={styles.label}>RFI Name</label>
          <input style={styles.input} value={rName} onChange={(e) => setRName(e.target.value)} placeholder="e.g. RFI 301 - Level 21 Amenity Space Power Needs" />

          <div style={styles.row2}>
            <div>
              <label style={styles.label}>Date Received</label>
              <input
                type="date"
                style={styles.input}
                value={rDateReceived}
                onChange={(e) => {
                  setRDateReceived(e.target.value);
                  setRDueDate(toLocalDateInputValue(addBusinessDaysLocal(parseLocalDateInput(e.target.value), 10)));
                }}
              />
            </div>
            <div>
              <label style={styles.label}>Due Date</label>
              <input
                type="date"
                style={styles.input}
                value={rDueDate}
                onChange={(e) => setRDueDate(e.target.value)}
              />
            </div>
          </div>
          <div>
            <label style={styles.label}>Notes (visible to reviewer, not included in the output report)</label>
            <textarea style={styles.textarea} value={rNotes} onChange={(e) => setRNotes(e.target.value)} placeholder="Optional note for the reviewer..." />
          </div>
          <EmployeePicker employees={employees} onSelect={(emp) => { setRReviewer(emp.name); setRReviewerEmail(emp.email); }} />
          <label style={styles.label}>Reviewer Name</label>
          <input style={styles.input} value={rReviewer} onChange={(e) => setRReviewer(e.target.value)} placeholder="e.g. Jane Doe" />
          <label style={styles.label}>Reviewer Email</label>
          <input style={styles.input} type="email" value={rReviewerEmail} onChange={(e) => setRReviewerEmail(e.target.value)} placeholder="reviewer@email.com" />
          <label style={styles.label}>RFI Document</label>

          <FileButton
            accept=".pdf,.doc,.docx"
            onChange={(e) => {
              const file = e.target.files[0] || null;
              setRRfiFile(file);
              if (file && !rName.trim()) {
                const nameWithoutExt = file.name.replace(/\.[^/.]+$/, '');
                setRName(nameWithoutExt);
              }
            }}
            label="+ Choose RFI Document"
          />

          {rRfiFile && (
            <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '12px' }}>{rRfiFile.name}</div>
          )}

          <label style={styles.label}>Primary Spec File</label>

          <FileButton
            accept=".pdf,.doc,.docx"
            onChange={(e) => {
              const file = e.target.files[0] || null;
              setRProjectDocsFile(file);
            }}
            label="+ Choose Primary Spec File"
          />

          {rProjectDocsFile && (
            <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '12px' }}>{rProjectDocsFile.name}</div>
          )}

          <label style={styles.label}>Drawings (optional, up to 20)</label>

          <FileButton accept=".pdf,image/*" multiple onChange={(e) => setRDrawings(Array.from(e.target.files))} label="+ Choose Drawings" />

          {rDrawings.length > 0 && (
            <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '12px' }}>{rDrawings.length} drawing(s) selected</div>
          )}

          <button style={styles.primaryBtn} onClick={submitRfi} disabled={submitting}>
            {submitting ? 'Logging...' : 'Log RFI & Notify Reviewer'}
          </button>
          <button style={styles.secondaryBtn} onClick={() => setMode('queue')}>Cancel</button>
        </div>
      )}

      {mode === 'new-for' && (
        <div style={styles.card}>
          <h3 style={{ marginTop: 0 }}>Log Field Report</h3>
          <p style={{ fontSize: '13px', color: '#6b7280', marginBottom: '16px' }}>
            For legacy reports from before AECWorks Ops was in use. New reports are pulled in automatically.
          </p>

          <ProjectPicker
            value={fProjectId} onChange={setFProjectId}
            projects={projects} offices={offices}
            showNewProject={showNewProject} setShowNewProject={setShowNewProject}
            newProjectNumber={newProjectNumber} setNewProjectNumber={setNewProjectNumber}
            newProjectName={newProjectName} setNewProjectName={setNewProjectName}
            newProjectOffice={newProjectOffice} setNewProjectOffice={setNewProjectOffice}
            createProject={createProject}
          />

          <label style={styles.label}>Report Number</label>
          <input style={styles.input} value={fNumber} onChange={(e) => setFNumber(e.target.value)} placeholder="e.g. FOR-2024-014" />

          <label style={styles.label}>Author</label>
          <input style={styles.input} value={fAuthorName} onChange={(e) => setFAuthorName(e.target.value)} placeholder="e.g. Jane Doe" />

          <label style={styles.label}>Date</label>
          <input style={styles.input} type="date" value={fDateField} onChange={(e) => setFDateField(e.target.value)} />

          <label style={styles.label}>Report PDF (optional)</label>

          <FileButton accept=".pdf" onChange={(e) => setFReportFile(e.target.files[0] || null)} label="+ Choose PDF" />

          {fReportFile && (
            <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '12px' }}>{fReportFile.name}</div>
          )}

          <button style={styles.primaryBtn} onClick={submitFor} disabled={submitting}>
            {submitting ? 'Logging...' : 'Log Field Report'}
          </button>
          <button style={styles.secondaryBtn} onClick={() => setMode('queue')}>Cancel</button>
        </div>
      )}

      {mode === 'manage-projects' && (
        <div style={styles.card}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <h3 style={{ margin: 0 }}>Manage Projects</h3>
            <button style={styles.secondaryBtn} onClick={() => setMode('queue')}>← Back to Queue</button>
          </div>

          <div style={{ ...styles.card, backgroundColor: '#f9fafb', marginBottom: '16px' }}>
            <div style={styles.label}>Add a Project</div>
            <div style={styles.row2}>
              <input style={styles.input} placeholder="Project Number (e.g. 20.10033.000)" value={newProjectNumber} onChange={(e) => setNewProjectNumber(e.target.value)} />
              <input style={styles.input} placeholder="Project Name" value={newProjectName} onChange={(e) => setNewProjectName(e.target.value)} />
            </div>
            {offices.length > 0 && (
              <select style={styles.select} value={newProjectOffice} onChange={(e) => setNewProjectOffice(e.target.value)}>
                <option value="">Office (optional)</option>
                {offices.map((o) => <option key={o.code} value={o.code}>{o.name || o.code}</option>)}
              </select>
            )}
            <button style={styles.primaryBtn} onClick={createProject}>Add Project</button>
          </div>

          <div style={{ ...styles.card, backgroundColor: '#f9fafb', marginBottom: '16px' }}>
            <div style={styles.label}>Upload Project List</div>
            <p style={{ fontSize: '12px', color: '#6b7280', marginBottom: '10px' }}>
              Upload a spreadsheet (.xlsx or .csv) with columns for Project Number and Project Name (Project Manager and Office are optional). Existing project numbers get updated; new ones get added.
            </p>
            <FileButton
              accept=".xlsx,.xls,.csv"
              onChange={(e) => { setImportFile(e.target.files[0] || null); setImportResult(null); }}
              label="+ Choose Spreadsheet"
            />
            {importFile && (
              <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '-8px', marginBottom: '10px' }}>{importFile.name}</div>
            )}
            <button style={styles.primaryBtn} onClick={importProjects} disabled={importing || !importFile}>
              {importing ? 'Importing...' : 'Import'}
            </button>
            {importResult && (
              <div style={{ marginTop: '10px', fontSize: '13px' }}>
                <div style={{ color: '#166534' }}>
                  ✓ {importResult.created} created, {importResult.updated} updated
                  {importResult.skipped > 0 ? `, ${importResult.skipped} skipped` : ''}.
                </div>
                {importResult.errors && importResult.errors.length > 0 && (
                  <div style={{ color: '#dc2626', marginTop: '6px' }}>
                    {importResult.errors.map((e, i) => <div key={i}>{e}</div>)}
                  </div>
                )}
              </div>
            )}
          </div>

          {projects.map((p) => (
            <div key={p._id} style={styles.listRow}>
              {editingProject?._id === p._id ? (
                <div style={{ width: '100%' }}>
                  <div style={styles.row2}>
                    <input style={styles.input} placeholder="Project Number" value={editForm.projectNumber} onChange={(e) => setEditForm({ ...editForm, projectNumber: e.target.value })} />
                    <input style={styles.input} placeholder="Project Name" value={editForm.projectName} onChange={(e) => setEditForm({ ...editForm, projectName: e.target.value })} />
                  </div>
                  <div style={styles.row2}>
                    <input style={styles.input} placeholder="Project Manager (optional)" value={editForm.projectManager} onChange={(e) => setEditForm({ ...editForm, projectManager: e.target.value })} />
                    <input style={styles.input} placeholder="Office (optional)" value={editForm.office} onChange={(e) => setEditForm({ ...editForm, office: e.target.value })} />
                  </div>
                  <button style={styles.primaryBtn} onClick={saveEditProject}>Save</button>
                  <button style={styles.secondaryBtn} onClick={() => setEditingProject(null)}>Cancel</button>
                </div>
              ) : (
                <>
                  <div>
                    <div style={styles.listRowTitle}>{p.projectNumber} — {p.projectName}</div>
                    <div style={styles.listRowMeta}>
                      {p.projectManager ? `PM: ${p.projectManager}` : ''}
                      {p.office ? ` · Office: ${p.office}` : ''}
                    </div>
                  </div>
                  <div>
                    <button style={styles.secondaryBtn} onClick={() => startEditProject(p)}>Edit</button>
                    <button
                      style={{ ...styles.secondaryBtn, color: '#dc2626', borderColor: '#fca5a5' }}
                      onClick={() => deleteProject(p)}
                    >
                      Delete
                    </button>
                  </div>
                </>
              )}
            </div>
          ))}
          {projects.length === 0 && (
            <p style={{ color: '#6b7280', fontSize: '14px' }}>No projects yet.</p>
          )}
        </div>
      )}
    </div>
  );
}